const SERVICE_ABBR = {
  sshd: { abbr: 'SSH', color: 'bg-blue-100 text-blue-700' },
  snmpd: { abbr: 'SNMP', color: 'bg-blue-100 text-blue-700' },
  httpd: { abbr: 'HTTP', color: 'bg-blue-100 text-blue-700' },
  dns: { abbr: 'DNS', color: 'bg-blue-100 text-blue-700' },
  ospfd: { abbr: 'OSPF', color: 'bg-purple-100 text-purple-700' },
  bgpd: { abbr: 'BGP', color: 'bg-purple-100 text-purple-700' },
  systemd: { abbr: 'SYS', color: 'bg-gray-100 text-gray-600' },
  kernel: { abbr: 'KERN', color: 'bg-gray-100 text-gray-600' },
  dhcp: { abbr: 'DHCP', color: 'bg-teal-100 text-teal-700' },
  catchall: { abbr: 'ALL', color: 'bg-gray-100 text-gray-500' },
};

// vista#312 — SNMP-capable devices with no profile assigned render in
// amber with a discrete warning glyph, so operators can spot at a
// glance which devices are discovery-detected but not yet bound to a
// poll profile (and won't show up in snmp_metrics).
const SNMP_UNCONFIGURED_PILL = {
  abbr: 'SNMP',
  color: 'bg-amber-100 text-amber-800 ring-1 ring-amber-300',
};

const SYSLOG_PILL = { abbr: 'LOG', color: 'bg-indigo-100 text-indigo-700' };
const MAX_INLINE = 3;

export default function ServicePills({
  services = [],
  hasSyslog = false,
  // Default is intentionally `undefined`, not `null`. The amber pill
  // is gated on `snmpProfileId === null` (the *explicit* "no profile"
  // signal), so callers that don't pass this prop (omitted = undefined)
  // get the default blue treatment. (#367 review note 3.)
  snmpProfileId,
}) {
  const pills = [];

  if (hasSyslog) {
    pills.push({ ...SYSLOG_PILL, name: 'Syslog Active' });
  }

  for (const svc of services) {
    const name = typeof svc === 'string' ? svc : svc.name;
    const displayName = typeof svc === 'string' ? svc : (svc.display_name || svc.name);
    // vista#312 — flag SNMP-capable devices without an assigned profile
    // so the operator can see at a glance which discovery-detected
    // services aren't actually being polled. Strict null match — a
    // future caller that omits the prop entirely (snmpProfileId =
    // undefined) must not silently render every snmpd device amber.
    // (#367 review note 3.)
    if (name === 'snmpd' && snmpProfileId === null) {
      pills.push({
        ...SNMP_UNCONFIGURED_PILL,
        name: `${displayName} — no SNMP profile assigned (device won't be polled)`,
        marker: '!',
      });
      continue;
    }
    const info = SERVICE_ABBR[name] || { abbr: name.slice(0, 4).toUpperCase(), color: 'bg-gray-100 text-gray-600' };
    pills.push({ ...info, name: displayName });
  }

  if (pills.length === 0) return <span className="text-xs text-gray-300">—</span>;

  const visible = pills.slice(0, MAX_INLINE);
  const overflow = pills.length - MAX_INLINE;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((p, i) => (
        <span key={i} title={p.name} className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none ${p.color}`}>
          {p.abbr}{p.marker ? <span className="ml-0.5 font-bold">{p.marker}</span> : null}
        </span>
      ))}
      {overflow > 0 && (
        <span title={pills.slice(MAX_INLINE).map((p) => p.name).join(', ')} className="inline-block rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium leading-none text-gray-600">
          +{overflow}
        </span>
      )}
    </div>
  );
}
